import {
  Component,
  HostListener,
  input,
  OnInit,
  output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Modal } from 'flowbite';
import { IBase, ICustomToggleOptions, IRestrictions, IRouteDetails } from 'src/app/models';
import { CustomToggleTone, NotificationTypes, RestrictionsQueryParam, RestrictionsTarget, RestrictionsType } from 'src/app/models/enums';
import { LiteralService, RouteService, StorageService, UtilsService } from 'src/app/services';
import { RestrictionsResolver } from 'src/app/resolvers';
import { initialRestrictionsState } from 'src/app/store/initialisation/restrictions';
import { IPagination, IRestrictionsState } from 'src/app/store/states';
import { TooltipPosition } from 'src/app/shared/tooltip/tooltip-position';
import { images } from 'src/images';
import { APP } from 'src/app/config/app';

@Component({
  selector: 'app-restrictions',
  templateUrl: './restrictions.component.html',
  styleUrl: './restrictions.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class RestrictionsComponent implements OnInit {
  readonly route = input<IRouteDetails>();
  readonly onGoBack = output();
  public restrictionsTarget = RestrictionsTarget.STOPS;
  public savedRestrictionsType = RestrictionsType.BLACK_LIST;
  public whiteListDraft: IRestrictions[] | null = null;
  public whiteListDraftPage = 1;
  public changeRestrictionTypeModal!: Modal;
  public changeRestrictionTypeShowed = false;
  public changingRestrictionType = false;
  public changeRestrictionTypeTitle = '';
  public changeRestrictionTypeText = '';
  public pendingRestrictionsType?: RestrictionsType;
  public stopOptions: { value: number; label: string }[] = [];
  public areaOptions: { value: number; label: string }[] = [];
  public restrictions = initialRestrictionsState;
  public deleteRestrictionModal!: Modal;
  public deleteRestrictionShowed = false;
  public deletingRestriction = false;
  public restrictionToDelete?: IRestrictions;
  public selectedRestriction?: IRestrictions;
  public townId?: number;
  public routeId?: number;

  public bulkSelectMode = false;
  public selectedRestrictionIds = new Set<number>();
  public bulkDeleteRestrictionsModal!: Modal;
  public bulkDeleteRestrictionsShowed = false;
  public deletingBulkRestrictions = false;
  public bulkDeleteText = '';

  private routeDetails?: IRouteDetails;

  public images = images;
  public RestrictionsType = RestrictionsType;
  public RestrictionsTarget = RestrictionsTarget;
  public readonly restrictionsTargetToggleOptions: ICustomToggleOptions<RestrictionsTarget> = [
    {
      value: RestrictionsTarget.STOPS,
      image: images.destination,
      descriptionKey: 'services.routes.restrictions.target.' + RestrictionsTarget.STOPS,
      tone: CustomToggleTone.SECONDARY,
    },
    {
      value: RestrictionsTarget.AREAS,
      image: images.routes.outlineSelected,
      descriptionKey: 'services.routes.restrictions.target.' + RestrictionsTarget.AREAS,
      tone: CustomToggleTone.SECONDARY,
    },
  ];
  public TooltipPosition = TooltipPosition;

  constructor(
    public literalService: LiteralService,
    private activatedRoute: ActivatedRoute,
    private routeService: RouteService,
    private restrictionsResolver: RestrictionsResolver,
    public utilsService: UtilsService,
    private storageService: StorageService,
    public router: Router,
  ) {}

  async ngOnInit(): Promise<void> {
    this.literalService.setTitle('sidebar.services', ' - NEMI Backoffice');
    this.restrictions = { ...initialRestrictionsState, value: [], isFetchingRestrictions: true };
    const size = await this.restrictionsResolver.getPageSize(this.activatedRoute.snapshot);
    this.restrictions = { ...this.restrictions, pagination: { ...this.restrictions.pagination, size } };
    const routeId = Number(this.activatedRoute.snapshot.params['routeId']);
    this.routeId = routeId;
    const [routeDetails, restrictions] = await Promise.all([
      this.routeService.getService(routeId) as Promise<IRouteDetails>,
      this.restrictionsResolver.resolve(this.activatedRoute.snapshot, this.getSearchValue()),
    ]);
    this.restrictions = restrictions;
    this.applyRouteDetails(routeDetails);
  }

  get restrictionsType(): RestrictionsType {
    return this.isWhiteListDraft ? RestrictionsType.WHITE_LIST : this.savedRestrictionsType;
  }

  get isWhiteListDraft(): boolean {
    return this.whiteListDraft !== null;
  }

  get displayedRestrictions(): IRestrictions[] {
    if (!this.whiteListDraft) return this.restrictions.value;
    const { actualPage, size } = this.whiteListDraftPagination;
    const start = (actualPage - 1) * size;
    return this.filteredWhiteListDraft.slice(start, start + size);
  }

  get currentPagination(): IPagination {
    return this.isWhiteListDraft ? this.whiteListDraftPagination : this.restrictions.pagination;
  }

  private get whiteListDraftPagination(): IPagination {
    const totalElements = this.filteredWhiteListDraft.length;
    const size = this.restrictions.pagination.size || APP.DEFAULT_PAGE_SIZE;
    const totalPages = Math.max(1, Math.ceil(totalElements / size));
    const actualPage = Math.min(Math.max(this.whiteListDraftPage, 1), totalPages);
    return {
      totalElements,
      totalPages,
      actualPage,
      size,
      numberOfElements: Math.max(0, Math.min(size, totalElements - (actualPage - 1) * size)),
      first: actualPage === 1,
      last: actualPage === totalPages,
    };
  }

  private get filteredWhiteListDraft(): IRestrictions[] {
    const draft = this.whiteListDraft ?? [];
    const searchValue = this.getSearchValue()?.trim().toLowerCase();
    if (!searchValue) return draft;
    const matches = (stop: IBase) => (stop.name ?? '').toLowerCase().includes(searchValue);
    return draft.filter(restriction =>
      matches(restriction.originStop) || restriction.restrictedDestinations.some(({ destination }) => matches(destination))
    );
  }

  private get isSavedStopsWhiteList(): boolean {
    return this.restrictionsTarget === RestrictionsTarget.STOPS && this.savedRestrictionsType === RestrictionsType.WHITE_LIST;
  }

  get isLastSavedWhiteListRestriction(): boolean {
    return this.isSavedStopsWhiteList
      && !this.getSearchValue()
      && (this.restrictions.pagination.totalElements ?? this.restrictions.value.length) <= 1;
  }

  private applyRouteDetails(routeDetails: IRouteDetails): void {
    this.routeDetails = routeDetails;
    this.savedRestrictionsType = routeDetails?.stopRestrictionType ?? RestrictionsType.BLACK_LIST;
    this.loadStops();
  }

  getDestinationNames(restriction: IRestrictions): string {
    return restriction.restrictedDestinations.map((d) => d.destination.name).join('\n');
  }

  private loadStops(): void {
    this.townId = this.routeDetails?.town?.id;
    this.stopOptions = this.sortOptionsByLabel((this.routeDetails?.stops ?? []).map((s) => ({
      value: s.id!,
      label: s.name ?? s.code ?? String(s.id),
    })));
    this.areaOptions = this.sortOptionsByLabel((this.routeDetails?.areas ?? [])
      .filter((area) => area.id != null)
      .map((area) => ({ value: area.id!, label: area.name })));
  }

  private sortOptionsByLabel(options: { value: number; label: string }[]): { value: number; label: string }[] {
    return options.sort((first, second) => APP.RESTRICTIONS_NAME_COLLATOR.compare(first.label, second.label));
  }

  get originOptions(): { value: number; label: string }[] {
    return this.restrictionsTarget === RestrictionsTarget.AREAS ? this.areaOptions : this.stopOptions;
  }

  async changeRestrictionsTarget(restrictionsTarget: RestrictionsTarget): Promise<void> {
    if (this.restrictionsTarget === restrictionsTarget) return;
    this.restrictionsTarget = restrictionsTarget;
    this.closeRestrictionDetails();
    this.cancelBulkSelectMode();
    await this.reloadRestrictions({ [RestrictionsQueryParam.SEARCH]: null, [RestrictionsQueryParam.PAGE]: null }, undefined);
  }

  private async refreshRestrictions(searchValue = this.getSearchValue()): Promise<void> {
    let restrictions = await this.resolveRestrictions(searchValue);
    const { actualPage, totalPages } = restrictions.pagination;
    if (actualPage > 1 && actualPage > totalPages) {
      await this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { [RestrictionsQueryParam.PAGE]: totalPages > 1 ? totalPages : null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
      restrictions = await this.resolveRestrictions(searchValue);
    }
    this.restrictions = restrictions;
    this.syncSelectedRestriction();
  }

  private resolveRestrictions(searchValue?: string): Promise<IRestrictionsState> {
    return this.restrictionsResolver.resolve(this.activatedRoute.snapshot, searchValue, this.restrictionsTarget);
  }

  async changePage(event: {
    isNext: boolean;
    specificPage?: number;
  }): Promise<void> {
    const current = this.currentPagination.actualPage;
    const newPage = event.specificPage ?? (event.isNext ? current + 1 : current - 1);
    if (this.isWhiteListDraft) {
      this.whiteListDraftPage = newPage;
      return;
    }
    await this.reloadRestrictions({ [RestrictionsQueryParam.PAGE]: newPage > 1 ? newPage : null });
  }

  async changePageSize(value: number): Promise<void> {
    const size = value > 0 ? value : APP.DEFAULT_PAGE_SIZE;
    await this.storageService.setRestrictionsPageSize(size);
    const queryParams = {
      [RestrictionsQueryParam.SIZE]: size === APP.DEFAULT_PAGE_SIZE ? null : size,
      [RestrictionsQueryParam.PAGE]: null,
    };
    if (this.isWhiteListDraft) {
      this.restrictions = { ...this.restrictions, pagination: { ...this.restrictions.pagination, size } };
      this.whiteListDraftPage = 1;
      await this.updateQueryParams(queryParams);
      return;
    }
    await this.reloadRestrictions(queryParams);
  }

  async search(value: string): Promise<void> {
    const queryParams = { [RestrictionsQueryParam.SEARCH]: value || null, [RestrictionsQueryParam.PAGE]: null };
    if (this.isWhiteListDraft) {
      this.whiteListDraftPage = 1;
      await this.updateQueryParams(queryParams);
      return;
    }
    await this.reloadRestrictions(queryParams, value || undefined);
  }

  private async updateQueryParams(queryParams: Params): Promise<void> {
    await this.router.navigate([], { relativeTo: this.activatedRoute, queryParams, queryParamsHandling: 'merge' });
  }

  private async reloadRestrictions(queryParams: Params, searchValue = this.getSearchValue()): Promise<void> {
    this.restrictions = { ...this.restrictions, isFetchingRestrictions: true, value: [] };
    await this.router.navigate([], { relativeTo: this.activatedRoute, queryParams, queryParamsHandling: 'merge' });
    await this.refreshRestrictions(searchValue);
  }

  private getSearchValue(): string | undefined {
    return this.activatedRoute.snapshot.queryParamMap.get(RestrictionsQueryParam.SEARCH) ?? undefined;
  }

  async onRestrictionAdded(event: {
    origin: IBase;
    destinations: IBase[];
  }): Promise<void> {
    if (this.isWhiteListDraft) {
      this.addToWhiteListDraft(event.origin, event.destinations);
      return;
    }
    if (!this.routeId) return;
    const restriction: IRestrictions = {
      originStop: event.origin,
      restrictedDestinations: event.destinations.map((destination) => ({
        destination,
      })),
    };
    try {
      await this.routeService.createRestrictions(this.routeId, restriction, this.restrictionsTarget);
      this.utilsService.showNotification(
        images.notifications.check,
        'services.routes.restrictions.actions.add.success',
        NotificationTypes.SUCCESS,
      );
      await this.refreshRestrictions();
    } catch {
      this.utilsService.showNotification(
        images.notifications.warning,
        'services.routes.restrictions.actions.add.error',
        NotificationTypes.DANGER,
      );
    }
  }

  private addToWhiteListDraft(origin: IBase, destinations: IBase[]): void {
    const draft = this.whiteListDraft ?? [];
    const listedRestriction = draft.find(restriction => restriction.originStop.id === origin.id);
    if (!listedRestriction) {
      const newRestriction = { originStop: origin, restrictedDestinations: destinations.map(destination => ({ destination })) };
      this.whiteListDraft = this.sortWhiteListDraft([...draft, newRestriction]);
      return;
    }
    const listedDestinationIds = new Set(listedRestriction.restrictedDestinations.map(restrictedDestination => restrictedDestination.destination.id));
    const updatedRestriction: IRestrictions = {
      ...listedRestriction,
      restrictedDestinations: [
        ...listedRestriction.restrictedDestinations,
        ...destinations.filter(destination => !listedDestinationIds.has(destination.id)).map(destination => ({ destination })),
      ],
    };
    this.whiteListDraft = this.sortWhiteListDraft(
      draft.map(restriction => restriction === listedRestriction ? updatedRestriction : restriction),
    );
  }

  private sortWhiteListDraft(draft: IRestrictions[]): IRestrictions[] {
    const compareNames = (first?: string, second?: string) =>
      APP.RESTRICTIONS_NAME_COLLATOR.compare(first ?? '', second ?? '');
    return draft
      .map(restriction => ({
        ...restriction,
        restrictedDestinations: [...restriction.restrictedDestinations].sort((first, second) =>
          compareNames(first.destination.name, second.destination.name),
        ),
      }))
      .sort((first, second) => compareNames(first.originStop.name, second.originStop.name));
  }

  private removeFromWhiteListDraft(restriction: IRestrictions): void {
    this.whiteListDraft = (this.whiteListDraft ?? []).filter(draftRestriction => draftRestriction.originStop.id !== restriction.originStop.id);
  }

  async showRestrictionDetails(restriction: IRestrictions): Promise<void> {
    if (this.isWhiteListDraft) return;
    this.selectedRestriction = restriction;
    // const originId = restriction.originStop.id;
    // if (!this.routeId || !originId) return;
    // const detail = await this.routeService.getRestriction(this.routeId, originId);
    // if (this.selectedRestriction?.originStop.id === originId) {
    //   this.selectedRestriction = detail;
    // }
  }

  closeRestrictionDetails(): void {
    this.selectedRestriction = undefined;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.selectedRestriction) return;
    const target = event.target as HTMLElement;
    if (
      target.closest('#restrictionDetailsContainer') ||
      target.closest('[data-restriction-row]')
    )
      return;
    this.closeRestrictionDetails();
  }

  async onRestrictionSaved(event: {
    originId: number;
    destinationIds: number[];
  }): Promise<void> {
    const restriction = this.selectedRestriction;
    if (
      !this.routeId ||
      !restriction ||
      restriction.originStop.id !== event.originId
    )
      return;

    const deletedRestrictions = restriction.restrictedDestinations.filter(
      (rd) => !event.destinationIds.includes(rd.destination.id!),
    );
    if (!deletedRestrictions.length) return;
    const removesWholeRestriction = deletedRestrictions.length === restriction.restrictedDestinations.length;
    if (removesWholeRestriction && await this.wouldEmptyWhiteList(1)) return;

    try {
      await this.routeService.deleteRestriction(
        this.routeId,
        [{ originStop: restriction.originStop, restrictedDestinations: deletedRestrictions }],
        this.restrictionsTarget,
      );
      this.utilsService.showNotification(
        images.notifications.check,
        'services.routes.restrictions.actions.delete.success',
        NotificationTypes.SUCCESS,
      );
      await this.refreshRestrictions();
      this.closeRestrictionDetails();
    } catch {
      this.utilsService.showNotification(
        images.notifications.warning,
        'services.routes.restrictions.actions.delete.error',
        NotificationTypes.DANGER,
      );
    }
  }

  private syncSelectedRestriction(): void {
    if (!this.selectedRestriction) return;
    const originId = this.selectedRestriction.originStop.id;
    this.selectedRestriction =
      this.restrictions.value.find((r) => r.originStop.id === originId) ??
      this.selectedRestriction;
  }

  async showDeleteRestrictionModal(restriction: IRestrictions): Promise<void> {
    if (this.isWhiteListDraft) {
      this.removeFromWhiteListDraft(restriction);
      return;
    }
    if (await this.wouldEmptyWhiteList(1)) return;
    this.restrictionToDelete = restriction;
    this.deleteRestrictionModal = new Modal(
      document.getElementById('deleteRestriction'),
      {
        placement: 'center',
        closable: false,
        onHide: () => (this.deleteRestrictionShowed = false),
        onShow: () => (this.deleteRestrictionShowed = true),
      },
    );
    this.deleteRestrictionModal.toggle();
  }

  async deleteRestrictionAction(): Promise<void> {
    const restriction = this.restrictionToDelete;
    const originId = restriction?.originStop.id;
    if (!this.routeId || !originId) return;
    try {
      this.deletingRestriction = true;
      await this.routeService.deleteRestriction(this.routeId, [restriction], this.restrictionsTarget);
      this.utilsService.showNotification(
        images.notifications.check,
        'services.routes.restrictions.actions.delete.success',
        NotificationTypes.SUCCESS,
      );
      if (this.selectedRestriction?.originStop.id === originId) {
        this.closeRestrictionDetails();
      }
      await this.refreshRestrictions();
    } catch {
      this.utilsService.showNotification(
        images.notifications.warning,
        'services.routes.restrictions.actions.delete.error',
        NotificationTypes.DANGER,
      );
    } finally {
      this.deletingRestriction = false;
      this.restrictionToDelete = undefined;
      this.deleteRestrictionModal.hide();
    }
  }

  private async wouldEmptyWhiteList(restrictionsToDelete: number): Promise<boolean> {
    if (!this.isSavedStopsWhiteList) return false;
    const savedRestrictionsCount = await this.countSavedRestrictions();
    if (restrictionsToDelete < savedRestrictionsCount) return false;
    this.utilsService.showNotification(images.notifications.warning, 'services.routes.restrictions.emptyWhiteList', NotificationTypes.WARNING);
    return true;
  }

  private async countSavedRestrictions(): Promise<number> {
    if (!this.getSearchValue()) return this.restrictions.pagination.totalElements ?? this.restrictions.value.length;
    if (!this.routeId) return 0;
    const response = await this.routeService.getRestrictions(this.routeId, 1, 1);
    return response?.totalElements ?? 0;
  }

  async onRestrictionTypeChangeRequested(requestedType: RestrictionsType): Promise<void> {
    if (requestedType === this.restrictionsType) return;
    if (this.isWhiteListDraft) {
      await this.cancelWhiteListDraft();
      return;
    }
    if (requestedType === RestrictionsType.WHITE_LIST) {
      this.startWhiteListDraft();
      return;
    }
    await this.showChangeRestrictionTypeModal(RestrictionsType.BLACK_LIST);
  }

  private startWhiteListDraft(): void {
    this.closeRestrictionDetails();
    this.cancelBulkSelectMode();
    this.whiteListDraftPage = 1;
    this.whiteListDraft = [];
  }

  async cancelWhiteListDraft(): Promise<void> {
    this.whiteListDraft = null;
    await this.reloadRestrictions({ [RestrictionsQueryParam.PAGE]: null });
  }

  async confirmWhiteListDraft(): Promise<void> {
    if (!this.whiteListDraft?.length) return;
    await this.showChangeRestrictionTypeModal(RestrictionsType.WHITE_LIST);
  }

  async showChangeRestrictionTypeModal(requestedType: RestrictionsType): Promise<void> {
    this.pendingRestrictionsType = requestedType;
    const literalKey = requestedType === RestrictionsType.WHITE_LIST
      ? 'services.routes.restrictions.changeType.toWhiteList'
      : 'services.routes.restrictions.changeType.toBlackList';
    const savedRestrictionsCount = await this.countSavedRestrictions();
    if (savedRestrictionsCount === 0) {
      await this.changeRestrictionTypeAction();
      return;
    }
    this.changeRestrictionTypeTitle = `${literalKey}.title`;
    this.changeRestrictionTypeText = this.literalService.get(`${literalKey}.text`, true, { num: savedRestrictionsCount.toString() });
    this.changeRestrictionTypeModal = new Modal(document.getElementById('changeRestrictionType'), {
      placement: 'center',
      closable: false,
      onHide: () => (this.changeRestrictionTypeShowed = false),
      onShow: () => (this.changeRestrictionTypeShowed = true),
    });
    this.changeRestrictionTypeModal.toggle();
  }

  async changeRestrictionTypeAction(): Promise<void> {
    const requestedType = this.pendingRestrictionsType;
    if (!this.routeId || !requestedType) return;
    const restrictions = requestedType === RestrictionsType.WHITE_LIST ? this.whiteListDraft ?? [] : [];
    if (requestedType === RestrictionsType.WHITE_LIST && !restrictions.length) return;
    try {
      this.changingRestrictionType = true;
      await this.routeService.changeRestrictionType(this.routeId, requestedType, restrictions);
      this.utilsService.showNotification(images.notifications.check, 'services.routes.restrictions.changeType.success', NotificationTypes.SUCCESS);
      this.whiteListDraft = null;
      await this.reloadRouteDetailsAndRestrictions();
    } catch {
      this.utilsService.showNotification(images.notifications.warning, 'services.routes.restrictions.changeType.error', NotificationTypes.DANGER);
    } finally {
      this.changingRestrictionType = false;
      this.pendingRestrictionsType = undefined;
      this.changeRestrictionTypeModal?.hide();
    }
  }

  private async reloadRouteDetailsAndRestrictions(): Promise<void> {
    if (!this.routeId) return;
    this.restrictions = { ...this.restrictions, isFetchingRestrictions: true, value: [] };
    const [routeDetails, restrictions] = await Promise.all([
      this.routeService.getService(this.routeId) as Promise<IRouteDetails>,
      this.resolveRestrictions(),
    ]);
    this.restrictions = restrictions;
    this.applyRouteDetails(routeDetails);
    this.syncSelectedRestriction();
  }

  async goBack(): Promise<void> {
    await this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { [RestrictionsQueryParam.PAGE]: null, [RestrictionsQueryParam.SEARCH]: null, [RestrictionsQueryParam.SIZE]: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    this.onGoBack.emit();
  }

  toggleBulkSelectMode(): void {
    this.bulkSelectMode = !this.bulkSelectMode;
    this.selectedRestrictionIds.clear();
    if (this.bulkSelectMode) this.closeRestrictionDetails();
  }

  cancelBulkSelectMode(): void {
    this.bulkSelectMode = false;
    this.selectedRestrictionIds.clear();
  }

  isRestrictionSelected(restriction: IRestrictions): boolean {
    return this.selectedRestrictionIds.has(restriction.originStop.id!);
  }

  toggleRestrictionSelection(restriction: IRestrictions): void {
    const originId = restriction.originStop.id!;
    if (this.selectedRestrictionIds.has(originId)) {
      this.selectedRestrictionIds.delete(originId);
    } else {
      this.selectedRestrictionIds.add(originId);
    }
  }

  get allRestrictionsSelected(): boolean {
    return (
      !!this.restrictions.value.length &&
      this.restrictions.value.every((r) => this.isRestrictionSelected(r))
    );
  }

  toggleSelectAllRestrictions(event: any): void {
    if (event.target.checked) {
      this.restrictions.value.forEach((r) =>
        this.selectedRestrictionIds.add(r.originStop.id!),
      );
    } else {
      this.selectedRestrictionIds.clear();
    }
  }

  async showBulkDeleteRestrictionsModal(): Promise<void> {
    if (!this.selectedRestrictionIds.size) return;
    if (await this.wouldEmptyWhiteList(this.selectedRestrictionIds.size)) return;
    this.bulkDeleteText = this.literalService.get(
      'services.routes.restrictions.bulkDelete.text',
      true,
      { num: this.selectedRestrictionIds.size.toString() },
    );
    this.bulkDeleteRestrictionsModal = new Modal(
      document.getElementById('bulkDeleteRestrictions'),
      {
        placement: 'center',
        closable: false,
        onHide: () => (this.bulkDeleteRestrictionsShowed = false),
        onShow: () => (this.bulkDeleteRestrictionsShowed = true),
      },
    );
    this.bulkDeleteRestrictionsModal.toggle();
  }

  async bulkDeleteRestrictionsAction(): Promise<void> {
    if (!this.routeId || !this.selectedRestrictionIds.size) return;
    const restrictionsToDelete = this.restrictions.value.filter((r) =>
      this.isRestrictionSelected(r),
    );
    try {
      this.deletingBulkRestrictions = true;
      await this.routeService.deleteRestriction(this.routeId, restrictionsToDelete, this.restrictionsTarget);
      this.utilsService.showNotification(
        images.notifications.check,
        'services.routes.restrictions.actions.deleteBulk.success',
        NotificationTypes.SUCCESS,
      );
      if (
        this.selectedRestriction &&
        this.selectedRestrictionIds.has(this.selectedRestriction.originStop.id!)
      ) {
        this.closeRestrictionDetails();
      }
      await this.refreshRestrictions();
      this.cancelBulkSelectMode();
    } catch {
      this.utilsService.showNotification(
        images.notifications.warning,
        'services.routes.restrictions.actions.deleteBulk.error',
        NotificationTypes.DANGER,
      );
    } finally {
      this.deletingBulkRestrictions = false;
      this.bulkDeleteRestrictionsModal.hide();
    }
  }
}
